import { Navigate, createBrowserRouter } from 'react-router-dom';
import { App } from './App';
import { PaywallGuard } from './components/PaywallGuard';
import { RootLayout } from './components/RootLayout';
import { KindPicker } from './features/handles-catalog/KindPicker';
import { HandlesCatalog } from './features/handles-catalog/HandlesCatalog';
import { HandleCard } from './features/handles-catalog/HandleCard';
import { HandlesShopsRoute } from './features/shops/HandlesShopsRoute';
import { DecorsShopsRoute } from './features/shops/DecorsShopsRoute';
import { KindPicker as DecorsKindPicker } from './features/decors-catalog/KindPicker';
import { DecorsFilter } from './features/decors-catalog/DecorsFilter';
import { MethodPicker as DecorsMethodPicker } from './features/decors-catalog/MethodPicker';
import { DecorsCatalog } from './features/decors-catalog/DecorsCatalog';
import { DecorsSearch } from './features/decors-catalog/DecorsSearch';
import { DecorCard } from './features/decors-catalog/DecorCard';
import { PhotoPicker } from './features/decors-catalog/PhotoPicker';
import { AlternativesScreen } from './features/decors-catalog/AlternativesScreen';
import { FurnitureEntry } from './features/furniture-catalog/FurnitureEntry';
import { FurnitureSearch } from './features/furniture-catalog/FurnitureSearch';
import { FurnitureCard } from './features/furniture-catalog/FurnitureCard';
import { FurnitureShopsRoute } from './features/shops/FurnitureShopsRoute';
import { PaintsCatalog } from './features/paint-catalog/PaintsCatalog';
import { PaintCard } from './features/paint-catalog/PaintCard';
import { PhotoPicker as PaintPhotoPicker } from './features/paint-catalog/PhotoPicker';
import { AlternativesScreen as PaintAlternativesScreen } from './features/paint-catalog/AlternativesScreen';
import { PaintShopsRoute } from './features/shops/PaintShopsRoute';
import { LangCityScreen } from './features/onboarding/LangCityScreen';
import { RegisterScreen } from './features/onboarding/RegisterScreen';
import { InstallScreen } from './features/install/InstallScreen';
import { CalculatorEntry } from './features/calculator/CalculatorEntry';
import { AnketaFormContainer } from './features/calculator/anketa/AnketaFormContainer';
import { QuotePageContainer } from './features/calculator/quote/QuotePageContainer';
import { SavedQuotesListContainer } from './features/calculator/quote/SavedQuotesListContainer';
import { ContractListContainer } from './features/contract/ContractListContainer';
import { ContractFormContainer } from './features/contract/ContractFormContainer';
import { ContractEditRoute, ContractPdfRoute } from './features/contract/routeWrappers';
import { PartnersCategories } from './features/partners/PartnersCategories';
import { PartnersList } from './features/partners/PartnersList';
import { PartnerCard } from './features/partners/PartnerCard';
import { PartnerReviews } from './features/partners/PartnerReviews';
import { PartnersPlaceholder } from './features/partners/PartnersPlaceholder';
import { TypePickerScreen } from './features/configurator/TypePickerScreen';
import { ConfiguratorScreen } from './features/configurator/ConfiguratorScreen';
import { FeedbackForm } from './features/feedback/FeedbackForm';
import { FeedbackConfirm } from './features/feedback/FeedbackConfirm';
import { ServicePicker } from './features/cooperation/ServicePicker';
import { CooperationForm } from './features/cooperation/CooperationForm';
import { CooperationConfirm } from './features/cooperation/CooperationConfirm';
import { CabinetHub } from './features/cabinet/CabinetHub';
import { ProfileEdit } from './features/cabinet/ProfileEdit';
import { SubscriptionScreen } from './features/cabinet/SubscriptionScreen';
import { ReferralScreen } from './features/cabinet/ReferralScreen';
import { CuttingStart } from './features/cutting/CuttingStart';
import { CuttingProject } from './features/cutting/CuttingProject';
import { CuttingHistory } from './features/cutting/CuttingHistory';
import { FEATURE_FLAGS } from './lib/featureFlags';

/**
 * Routing-схема:
 * - `/` — временная заглушка (App.tsx) до создания Главной (Блок Главной — Phase 8+).
 * - Block 4 (Ручки/Крючки):
 *   - `/handles` — Экран 2.4.1 KindPicker.
 *   - `/handles/:kind` — Экран 2.4.2 Каталог.
 *   - `/handles/:kind/:globalId` — Экран 2.4.3 Карточка.
 *   - `/handles/:kind/:globalId/shops` — Экран 2.4.4 Магазины.
 * - Block 2 (Декор):
 *   - `/decors` — Экран 2.2.1 KindPicker (Фасады / Столешницы / Корпус).
 *   - `/decors/:kind` — Экран фильтра (DecorsFilter, PR-FLOW/ADR-0043): чипы
 *     subcategory/brand/type + «Далее» → `/method?<f>`. Фильтр сразу после kind,
 *     ДО метод-пикера; опционален; пробрасывается во все ветки подбора.
 *   - `/decors/:kind/method` — Экран 2.2.2 MethodPicker: 3 метода подбора
 *     (фото / название / каталог). Пробрасывает `?<f>` verbatim в photo/catalog.
 *   - `/decors/:kind/catalog` — Экран 2.2.6 (DecorsCatalog): список по фильтру
 *     (Возм.3 «Полистай каталог»).
 *   - `/decors/:kind/photo` — PhotoPicker (Phase 7, Экран 2.2.3): подбор ВНУТРИ
 *     фильтра (фильтр из URL → match, ADR-0043).
 *   - `/decors/:kind/search` — DecorsSearch (ADR-0067, Экран 2.2.2-search): живой
 *     поиск по названию (?name=) ВНУТРИ активного фильтра из URL.
 *   - `/decors/:kind/:globalId` — DecorCard (Phase 7, Экран 2.2.7).
 *   - `/decors/:kind/:globalId/alternatives?mode=...` — Alternatives (Phase 7, Экран 2.2.8).
 *   - `/decors/:kind/:globalId/shops` — ShopsList (Phase 8 — shared `features/shops/` per ADR-0007).
 * - Block 0 Phase 2 (Онбординг, ADR-0012):
 *   - `/onboarding/lang-city` — Stage 1, выбор языка + города (guest mode).
 *   - `/onboarding/register` — Stage 2, full registration form (Phase 4 soft-gate
 *     entry-point; в Phase 2 только debug-вход через прямой URL).
 *   - При `FEATURE_FLAGS.ONBOARDING_ENABLED=false` оба route рендерят
 *     `<Navigate to="/" />` — build-time kill-switch без code-revert.
 * - Block 0 Phase 3 (PWA install, ADR-0013):
 *   - `/install` — Экран 2.0.PWA «Загрузи приложение». Container выбирает
 *     ветку рендера (standalone redirect / iOS instructions / native prompt /
 *     fallback) на основе `useStandaloneMode` + `useInstallPrompt` + `isIOS()`.
 *   - При `FEATURE_FLAGS.PWA_INSTALL_ENABLED=false` route рендерит
 *     `<Navigate to="/" />` — build-time kill-switch без code-revert
 *     (см. ADR-0013 §«Применение/Rollback»).
 * - Block 12 (Калькулятор, ADR-0019):
 *   - `/calculator` — Entry Screen 2.12.1 (CalculatorEntry: 8 anketa-карточек +
 *     DraftBanner + КП-кнопка). Phase A6 закрыл B-052: `/calculator-stub`
 *     placeholder удалён, real Block 12 в production-пути с самого entry.
 *   - `/calculator/anketa/:type` — AnketaForm template engine (Phase B3 Part C,
 *     ADR-0020). URL guard на 8 valid types; optional `?draftId=` hydrate из IDB,
 *     `?pickerFor=...&pickedId=...` return-flow от Block 2/5/8 cross-link.
 * - Block 13 (Договор, PR-G Phase 6 wires routes; ADR-0025):
 *   - `/contracts` — Экран 2.13.1 «Мои договоры» (ContractListContainer).
 *     Entry-screen для блока + paywall landing для trial/paid в Home.tsx.
 *   - `/contracts/new` — Экран 2.13.2 «Создание договора» (ContractFormContainer
 *     без contractId — генерирует новый UUID). Optional `?fromQuote=<uuid>` —
 *     seed композиции из saved-quote IDB (Phase 3 useFromQuoteSeeding hook).
 *   - `/contracts/:id/edit` — Экран 2.13.2 в edit mode (ContractFormContainer
 *     с contractId из URL params через ContractEditRoute wrapper). Hydrate
 *     из IDB через `loadContractDraftFromIdb`.
 *   - `/contracts/:id/pdf` — Экран 2.13.3 «Готовый PDF» (ContractPdfPreviewContainer
 *     через ContractPdfRoute wrapper). Render jsPDF + blob URL embed.
 *   - **Closes B-116** (Block 12 Phase D8 placeholder) + ADR-0014 §Q5 stub flow.
 *     Routes `/contract` и `/contract-stub` удалены в этой фазе.
 * - Block 3 (Краски):
 *   - `/paint` — Phase 3 каталог (один paint-домен, без kind picker'а — ADR-0010 §1).
 *   - `/paint/photo` — Phase 4 PhotoPicker (entry point из App.tsx «Подобрать
 *     краску по фото»; нет привязки к конкретному paint).
 *   - `/paint/:globalId` — Phase 4 PaintCard.
 *   - `/paint/:globalId/alternatives?mode=...` — Phase 4 AlternativesScreen.
 *   - `/paint/:globalId/shops` — Phase 5 (ShopsList wire-up, ADR-0007 thin wrapper).
 * - Block 6 (Конфигуратор, ADR-0015):
 *   - `/configurator` — Экран 2.6.1 TypePickerScreen (выбор 1 из 9 типов мебели).
 *   - `/configurator/:type` — Экран 2.6.2 ConfiguratorScreen (Phase 1 stub;
 *     Phase 2 — полноценная SVG-визуализация). URL guard внутри Container:
 *     невалидный `:type` → `<Navigate to="/configurator" replace />`.
 * - Block 10 (Что улучшить?, ADR-0018):
 *   - `/feedback` — Экран 2.10.1 FeedbackForm (Phase 4 Step 1 + Step 2).
 *   - `/feedback/sent` — Экран 2.10.2 FeedbackConfirm (Phase 4 Step 3 «Спасибо!»).
 * - Block 12 deeplink targets (ADR-0099, furniture amended ADR-0100 — жёлтая
 *   кнопка «Найти и узнать цену»):
 *   - `/furniture` — Block 5 «Умный фурнитурщик» вход (FurnitureEntry, экран
 *     2.5.6, ADR-0068). PR #199 ошибочно вёл на placeholder-роут; исправлено в ADR-0100.
 *   - `/partners` — Block 8 «Партнёры» каталог подрядчиков (PartnersCategories,
 *     экран 2.8.1, ADR-0080 Фаза D1). `/partners/list`+`/partners/review` —
 *     placeholder'ы до Фаз D2/E.
 * См. BACKLOG B-007: URL namespace `<plural>/<singular>` пересмотр.
 */
/**
 * PR-D-D Closeout: single RootLayout wraps all routes — provides ScrollToTop
 * cross-route effect (P-047) без editing каждого page component. Children
 * массив сохраняет flat routing semantics (each route остаётся top-level).
 */
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: <App />,
      },
      {
        path: '/onboarding/lang-city',
        element: FEATURE_FLAGS.ONBOARDING_ENABLED ? (
          <LangCityScreen />
        ) : (
          <Navigate to="/" replace />
        ),
      },
      {
        path: '/onboarding/register',
        element: FEATURE_FLAGS.ONBOARDING_ENABLED ? (
          <RegisterScreen />
        ) : (
          <Navigate to="/" replace />
        ),
      },
      {
        path: '/install',
        element: FEATURE_FLAGS.PWA_INSTALL_ENABLED ? (
          <InstallScreen />
        ) : (
          <Navigate to="/" replace />
        ),
      },
      {
        // PR-G Phase 7.2 — все Block 12 (/calculator*) и Block 13 (/contracts*)
        // routes wrapped в <PaywallGuard/>. Direct URL access (bookmark / back /
        // deep-link) при subscription ∈ {grace, expired_readonly, archived, null}
        // → redirect '/'. trial / paid → render verbatim. ADR-0014 §«Решение» Q5
        // semantics enforced на route-level (раньше — только в Home.tsx tap).
        path: '/calculator',
        element: (
          <PaywallGuard>
            <CalculatorEntry />
          </PaywallGuard>
        ),
      },
      {
        // Block 12 Phase B3 Part C Step 26 — AnketaForm template entry.
        // URL guard внутри Container: invalid `:type` → Navigate('/calculator', replace).
        // Optional query `?draftId=<uuid>` для IDB hydrate, `?pickerFor=...&pickedId=...`
        // для return-flow из Block 2/5/8 cross-block селекции (ADR-0020 §Q10+§Q11).
        path: '/calculator/anketa/:type',
        element: (
          <PaywallGuard>
            <AnketaFormContainer />
          </PaywallGuard>
        ),
      },
      {
        // Block 12 PR-D-A Phase D1 Step 1 — Quote (КП) builder, ADR-0005 Container/Presenter.
        // URL: /calculator/quote?projectId=<uuid> from AnketaFormContainer onCreateQuote
        // (ADR-0020 §Q11). Phase D5 wires anketa→quote seeding + draft autosave.
        path: '/calculator/quote',
        element: (
          <PaywallGuard>
            <QuotePageContainer />
          </PaywallGuard>
        ),
      },
      {
        // Block 12 PR-H5b (ADR-0032) — SavedQuotesList browse screen.
        // PaywallGuard consistent с другими /calculator routes.
        path: '/calculator/quote/saved',
        element: (
          <PaywallGuard>
            <SavedQuotesListContainer />
          </PaywallGuard>
        ),
      },
      {
        // Block 13 PR-G Phase 6 — entry route, Экран 2.13.1 «Мои договоры».
        // Home.tsx tap «Договор на мебель» landing point (trial/paid + TEST_DEPLOY_BYPASS).
        // Empty state CTA «+ Создать договор» → /contracts/new.
        path: '/contracts',
        element: (
          <PaywallGuard>
            <ContractListContainer />
          </PaywallGuard>
        ),
      },
      {
        // Block 13 PR-G Phase 6 — Экран 2.13.2 «Создание договора» (новый draft).
        // Optional `?fromQuote=<uuid>` для seed из Block 12 saved-quote.
        // ВАЖНО: регистрируется ДО `/contracts/:id/edit`, иначе `:id` поглотит «new».
        path: '/contracts/new',
        element: (
          <PaywallGuard>
            <ContractFormContainer />
          </PaywallGuard>
        ),
      },
      {
        // Block 13 PR-G Phase 6 — Экран 2.13.2 в edit mode.
        // ContractEditRoute wrapper извлекает :id из useParams и передаёт
        // в Container (Container hydrate'ит из IDB по contractId).
        path: '/contracts/:id/edit',
        element: (
          <PaywallGuard>
            <ContractEditRoute />
          </PaywallGuard>
        ),
      },
      {
        // Block 13 PR-G Phase 6 — Экран 2.13.3 «Готовый PDF».
        // ContractPdfRoute wrapper извлекает :id; Container loads contract +
        // template + рендерит PDF через jsPDF + blob URL embed.
        path: '/contracts/:id/pdf',
        element: (
          <PaywallGuard>
            <ContractPdfRoute />
          </PaywallGuard>
        ),
      },
      {
        path: '/handles',
        element: <KindPicker />,
      },
      {
        path: '/handles/:kind',
        element: <HandlesCatalog />,
      },
      {
        path: '/handles/:kind/:globalId',
        element: <HandleCard />,
      },
      {
        path: '/handles/:kind/:globalId/shops',
        element: <HandlesShopsRoute />,
      },
      {
        path: '/decors',
        element: <DecorsKindPicker />,
      },
      {
        // PR-FLOW (ADR-0043): фильтр-экран сразу после выбора kind, ДО метод-
        // пикера. Чипы subcategory/brand/type + «Далее» → `/decors/:kind/method`
        // (с выбранными фильтрами в URL). Фильтр опционален.
        path: '/decors/:kind',
        element: <DecorsFilter />,
      },
      {
        // PR-FLOW: Экран 2.2.2 метод-пикер. Пробрасывает `?<f>` verbatim в
        // photo/catalog. ВАЖНО: регистрируется ДО `/decors/:kind/:globalId`
        // (как photo/catalog), иначе `:globalId` поглотит "method" как verbatim
        // Global_ID.
        path: '/decors/:kind/method',
        element: <DecorsMethodPicker />,
      },
      {
        // Экран 2.2.6 — список каталога (DecorsCatalog, Возм.3 «Полистай
        // каталог»). Читает фильтры из URL, «Найти» показывает список inline.
        // ВАЖНО: регистрируется ДО `/decors/:kind/:globalId` (как photo),
        // иначе `:globalId` поглотит "catalog" как verbatim Global_ID.
        path: '/decors/:kind/catalog',
        element: <DecorsCatalog />,
      },
      {
        // ВАЖНО: photo route регистрируется ДО `/decors/:kind/:globalId`,
        // иначе `:globalId` поглотит "photo" как verbatim Global_ID.
        path: '/decors/:kind/photo',
        element: <PhotoPicker />,
      },
      {
        // Экран 2.2.2-search «Найти декор по названию» (ADR-0067, DecorsSearch).
        // Живой поиск по name (?name=) ВНУТРИ активного фильтра из URL.
        // ВАЖНО: регистрируется ДО `/decors/:kind/:globalId` (как photo/catalog),
        // иначе `:globalId` поглотит "search" как verbatim Global_ID.
        path: '/decors/:kind/search',
        element: <DecorsSearch />,
      },
      {
        path: '/decors/:kind/:globalId',
        element: <DecorCard />,
      },
      {
        path: '/decors/:kind/:globalId/alternatives',
        element: <AlternativesScreen />,
      },
      {
        path: '/decors/:kind/:globalId/shops',
        element: <DecorsShopsRoute />,
      },
      // ── Блок 5 «Фурнитура и магазины» (ADR-0068, Вариант 1) ──────────────
      // /furniture — вход 2.5.6; /furniture/search — поиск 2.5.7; карточка 2.5.8.
      {
        path: '/furniture',
        element: <FurnitureEntry />,
      },
      {
        // ВАЖНО: search регистрируется ДО `/furniture/:globalId`, иначе `:globalId`
        // поглотит "search" как verbatim Global_ID (как /paint/photo, /decors/:kind/search).
        path: '/furniture/search',
        element: <FurnitureSearch />,
      },
      {
        // react-router v7 ранжирует по специфичности статических сегментов
        // независимо от порядка в массиве; порядок оставлен для читаемости.
        // E7: реальный экран магазинов (общий shops-модуль, ADR-0068 §5).
        path: '/furniture/:globalId/shops',
        element: <FurnitureShopsRoute />,
      },
      {
        path: '/furniture/:globalId',
        element: <FurnitureCard />,
      },
      {
        path: '/paint',
        element: <PaintsCatalog />,
      },
      {
        // ВАЖНО: photo route регистрируется ДО `/paint/:globalId`,
        // иначе `:globalId` поглотит "photo" как verbatim Global_ID.
        path: '/paint/photo',
        element: <PaintPhotoPicker />,
      },
      {
        path: '/paint/:globalId',
        element: <PaintCard />,
      },
      {
        path: '/paint/:globalId/alternatives',
        element: <PaintAlternativesScreen />,
      },
      {
        path: '/paint/:globalId/shops',
        element: <PaintShopsRoute />,
      },
      {
        path: '/configurator',
        element: FEATURE_FLAGS.CONFIGURATOR_ENABLED ? (
          <TypePickerScreen />
        ) : (
          <Navigate to="/" replace />
        ),
      },
      {
        path: '/configurator/:type',
        element: FEATURE_FLAGS.CONFIGURATOR_ENABLED ? (
          <ConfiguratorScreen />
        ) : (
          <Navigate to="/" replace />
        ),
      },
      {
        path: '/feedback',
        element: <FeedbackForm />,
      },
      {
        path: '/feedback/sent',
        element: <FeedbackConfirm />,
      },
      {
        // Block 9 «Сотрудничество» (ADR-0086) — публичный поток заявки поставщика
        // (БЕЗ PaywallGuard: открыт для регистрации новых поставщиков, §1a).
        //   /cooperation        — Экран 2.9.1 «Выбор услуги» (ServicePicker, entry).
        //   /cooperation/apply  — Экран 2.9.2 «Анкета поставщика» (?service=<услуга>).
        //   /cooperation/sent   — Экран 2.9.3 «Заявка принята» (CooperationConfirm).
        path: '/cooperation',
        element: <ServicePicker />,
      },
      {
        path: '/cooperation/apply',
        element: <CooperationForm />,
      },
      {
        path: '/cooperation/sent',
        element: <CooperationConfirm />,
      },
      {
        // Block 11 «Личный кабинет» Phase 11.A — Экран 2.11.1 хаб (CabinetHub).
        // Front-first: читает authStore (profile/city/subscription) + GET /api/me;
        // сообщения — мок (0 → пустое состояние). Дочерние экраны ЛК
        // (/cabinet/profile 2.11.2, /cabinet/subscription 2.11.3,
        // /cabinet/referral 2.11.4, /cabinet/messages) — навигация-намерение,
        // реализуются в 11.B–11.D.
        path: '/cabinet',
        element: <CabinetHub />,
      },
      {
        // Block 11 «Личный кабинет» Phase 11.B — Экран 2.11.2 «Редактирование
        // профиля» (ProfileEdit, ADR-0091). Дочерний экран ЛК (VideoButton НЕ
        // дублируется, P-040). Форма пред-заполняется из authStore; PUT /api/profile
        // (MSW-мок) эхо → синк authStore. Город — только свой регион (P-008).
        // Телефон — отдельный OTP-под-флоу (мок, P-010).
        path: '/cabinet/profile',
        element: <ProfileEdit />,
      },
      {
        // Block 11 «Личный кабинет» Phase 11.C — Экран 2.11.3 «Подписка / оплата»
        // (SubscriptionScreen, ADR-0093). Дочерний экран ЛК (VideoButton НЕ
        // дублируется, P-040). Статус подписки + тариф (GET plans) + промо-preview
        // (POST quote) + заглушка оплаты + история (GET history) — всё на MSW-моках.
        // ДЕНЬГИ: фронт не шлёт сумму (P-006); checkout НЕ вызывается (заглушка).
        // Transient `?promo=AUTORENEW_3D` пред-заполняет промо (с хаба «−30%», R3).
        path: '/cabinet/subscription',
        element: <SubscriptionScreen />,
      },
      {
        // Block 11 «Личный кабинет» Phase 11.D — Экран 2.11.4 «Реферальная
        // программа» (ReferralScreen, ADR-0094). Дочерний экран ЛК (VideoButton
        // НЕ дублируется, P-040). Хаб «Сэкономь 50% — приведи друга» (2.11.1)
        // ведёт сюда. Персональная ссылка + Web Share / fallback copy + ТОЛЬКО
        // счётчики рефералов (P-009, без ПДн друзей) + тизер промо −50%. GET
        // /api/referral на MSW-моке. Последний экран Блока 11.
        path: '/cabinet/referral',
        element: <ReferralScreen />,
      },
      {
        // Block 8 «Партнёры» — каталог подрядчиков (ADR-0080, Фаза D1).
        // Экран 2.8.1 «Категории» (ADR-0080 Phase D1). Landing для жёлтой кнопки
        // «Найти и узнать цену» (deeplink partner, ADR-0099 — без ?category=).
        // Специфичные пути (`/list`, `/review`) — ДО параметрического `/:id`.
        path: '/partners',
        element: <PartnersCategories />,
      },
      {
        // Фаза D2 — реальный экран 2.8.2 «Список партнёров категории»
        // (PartnersList: ?category= из URL + селект города + подтумблер «Распил»).
        path: '/partners/list',
        element: <PartnersList />,
      },
      {
        // Фаза E заменит реальной формой 2.8.4 «Что улучшить подрядчику».
        path: '/partners/review',
        element: <PartnersPlaceholder title="Что улучшить подрядчику" />,
      },
      {
        // Фаза D3 — саб-экран 2.8.3 «Отзывы партнёра» (empty «Отзывов пока нет»;
        // реальные отзывы + эндпоинт — Фаза E). Статический хвост `/reviews` — ДО
        // параметрической `/:id` ниже (специфичные сегменты раньше параметрических).
        path: '/partners/:id/reviews',
        element: <PartnerReviews />,
      },
      {
        // Фаза D3 — детальная карточка 2.8.3 (разрежённая: название/категория/
        // контакты «Написать» P-031/кнопка «Отзывы»). ВАЖНО: параметрический `/:id` —
        // ПОСЛЕ специфичных `/list`, `/review`, `/:id/reviews`.
        path: '/partners/:id',
        element: <PartnerCard />,
      },
      {
        // Block 14 (Раскрой, PR #1 EP-1 шасси) — авторский модуль (ADR-0061),
        // namespace UI `/cutting/*` (ADR-0062). Маршруты eager по конвенции репо
        // (весь router без React.lazy). Кнопка на Главной — PR #3.
        // - `/cutting` — Экран 2.14.1 «Начало» (3 карточки).
        // - `/cutting/project` — Экран 2.14.2 «Рабочий экран» (каркас 6 вкладок).
        // - `/cutting/history` — Экран 2.14.3 «История» (пустое состояние).
        path: '/cutting',
        element: <CuttingStart />,
      },
      {
        path: '/cutting/project',
        element: <CuttingProject />,
      },
      {
        path: '/cutting/history',
        element: <CuttingHistory />,
      },
    ],
  },
]);
